import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, InputNumber, Button, Card, Switch, Row, Col, Radio, Select, Typography, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { useTemplates } from 'hooks/api/useTemplates';
import NewMobilePreview from 'views/app-views/whatsapp/Campaign/newMobilePreview';
import SelectMediaModel from 'views/app-views/whatsapp/Campaign/CampainComps/SelectMediaModel';
const { TextArea } = Input;
const { Text } = Typography;

const HEADER_MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

/** Split a raw template into { header, body, footer, buttons } sections (for the preview). */
const extractSections = (raw) => {
    const out = { header: null, body: null, footer: null, buttons: null };
    raw?.components?.forEach((s) => {
        if (s.type === 'HEADER') out.header = s;
        else if (s.type === 'BODY') out.body = s;
        else if (s.type === 'FOOTER') out.footer = s;
        else if (s.type === 'BUTTONS') out.buttons = s;
    });
    return out;
};

/** Count body variables ({{1}}, {{2}}, ...) in a template's BODY component. */
const countBodyParams = (template) => {
    const body = template?.components?.find((c) => c.type === 'BODY');
    if (!body?.text) return 0;
    const matches = body.text.match(/{{\s*(\d+)\s*}}/g) || [];
    return matches.reduce((max, token) => {
        const n = parseInt(token.replace(/[^0-9]/g, ''), 10);
        return Number.isNaN(n) ? max : Math.max(max, n);
    }, 0);
};

/** Returns the HEADER media format (IMAGE/VIDEO/DOCUMENT) or null for text/none. */
const headerMediaFormat = (template) => {
    const header = template?.components?.find((c) => c.type === 'HEADER');
    return header && HEADER_MEDIA_FORMATS.includes(header.format) ? header.format : null;
};

const ChatBotIdealTimer = ({ UserData, token, api }) => {
    const [form] = Form.useForm();
    const [minutes, setMinutes] = useState(0);
    const [customMessage, setCustomMessage] = useState('');
    const [showFields, setShowFields] = useState(false);
    const [resetOnNewMesage, setResetOnNewMesage] = useState(false);
    const [loading, setLoading] = useState(false);

    const [messageType, setMessageType] = useState('text');
    const [templateCategory, setTemplateCategory] = useState('MARKETING');
    const [templateName, setTemplateName] = useState(undefined);
    const [templateLanguage, setTemplateLanguage] = useState('en_US');
    const [bodyParams, setBodyParams] = useState([]);
    const [headerMedia, setHeaderMedia] = useState({ source: 'id', value: '', name: '' });
    const [openMediaModel, setOpenMediaModel] = useState(false);

    const handleMediaSelect = (mediaId, name) => {
        setHeaderMedia((m) => ({
            ...m,
            value: mediaId,
            name: name,
        }));
        setOpenMediaModel(false);
    };

    const { templates = [], isLoading: templatesLoading } = useTemplates();

    const selectedTemplate = useMemo(
        () => templates.find((t) => t.name === templateName),
        [templates, templateName]
    );
    const templateSections = useMemo(
        () => (selectedTemplate ? extractSections(selectedTemplate) : null),
        [selectedTemplate]
    );
    const mediaFormat = headerMediaFormat(selectedTemplate);
    const requiredBodyCount = countBodyParams(selectedTemplate);

    // Templates filtered by approved status + selected category (non-carousel only).
    const templateOptions = useMemo(
        () =>
            templates
                .filter(
                    (t) =>
                        t.status === 'APPROVED' &&
                        t.category === templateCategory &&
                        !t.components?.some((c) => c.type === 'CAROUSEL')
                )
                .map((t) => ({ value: t.name, label: `${t.name} (${t.language})` })),
        [templates, templateCategory]
    );

    // Keep the body-param input list in sync with the selected template's variable count.
    useEffect(() => {
        setBodyParams((prev) => {
            const next = [...prev];
            next.length = requiredBodyCount;
            for (let i = 0; i < requiredBodyCount; i += 1) {
                if (next[i] == null) next[i] = '';
            }
            return next;
        });
    }, [requiredBodyCount]);

    // Once templates load, align the category radio with the saved template's category.
    useEffect(() => {
        if (templateName && templates.length > 0) {
            const matched = templates.find((t) => t.name === templateName);
            if (matched?.category) setTemplateCategory(matched.category);
        }
    }, [templates, templateName]);

    const fetchIdealTimer = async () => {
        try {
            const response = await apiClient.get(`${api}get-ideal-timer-by-user/${UserData?.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = response.data?.data;
            if (data) {
                setMinutes(data.minutes);
                setCustomMessage(data.message);
                setShowFields(data.status === 1);
                setResetOnNewMesage(Boolean(data.reset_on_message));
                setMessageType(data.message_type || 'text');
                form.setFieldsValue({ minutes: data.minutes, message: data.message });

                const cfg = data.template_config || {};
                setTemplateName(cfg.template_name || undefined);
                setTemplateLanguage(cfg.template_language || 'en_US');
                setBodyParams((cfg.body_params || []).map((p) => p?.value ?? ''));
                setHeaderMedia({
                    source: cfg.header_media_source || 'id',
                    value: cfg.header_media_value || '',
                    name: cfg.header_media_name || '',
                });
            }
        } catch (error) {
            message.error('Failed to fetch Idle timer data.');
        }
    };

    useEffect(() => {
        fetchIdealTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCategoryChange = (e) => {
        setTemplateCategory(e.target.value);
        setTemplateName(undefined);
        setBodyParams([]);
        setHeaderMedia({ source: 'id', value: '', name: '' });
    };

    const handleTemplateSelect = (name) => {
        setTemplateName(name || undefined);
        const matched = templates.find((t) => t.name === name);
        setTemplateLanguage(matched?.language || 'en_US');
        setHeaderMedia({ source: 'id', value: '', name: '' });
    };

    const buildTemplateConfig = () => {
        const config = {
            template_name: templateName || '',
            template_language: templateLanguage || 'en_US',
            template_category: templateCategory,
            body_params: bodyParams.map((value) => ({ source: 'static', value: value ?? '' })),
        };
        if (mediaFormat) {
            config.header_media_source = headerMedia.source || 'id';
            config.header_media_value = headerMedia.value || '';
            config.header_format = mediaFormat.toLowerCase();
            config.header_media_name = headerMedia.name || '';
        }
        return config;
    };

    const handleSubmit = async () => {
        if (showFields && messageType === 'template' && !templateName) {
            message.error('Please select a template.');
            return;
        }
        if (showFields && messageType === 'text' && !customMessage?.trim()) {
            message.error('Please enter your message.');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                user_id: UserData?.id,
                minutes,
                status: showFields === true ? 1 : 0,
                reset_on_message: resetOnNewMesage === true,
                message_type: messageType,
                message: messageType === 'text' ? customMessage : null,
                template_config: messageType === 'template' ? buildTemplateConfig() : null,
            };
            const response = await apiClient.post(`${api}store-ideal-timer`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data?.status) {
                message.success('Idle timer saved successfully!');
            }
        } catch (error) {
            message.error('Failed to save Idle timer.');
        } finally {
            setLoading(false);
        }
    };

    const renderTemplateConfig = () => (
        <>
            <Form.Item label="Template">
                <Select
                    showSearch
                    allowClear
                    loading={templatesLoading}
                    placeholder="Search and select a template"
                    value={templateName}
                    onChange={handleTemplateSelect}
                    options={templateOptions}
                    filterOption={(input, opt) => opt?.label?.toLowerCase().includes(input.toLowerCase())}
                    style={{ width: '100%' }}
                />
            </Form.Item>

            {requiredBodyCount > 0 && (
                <Form.Item label="Template Variables">
                    {bodyParams.map((value, index) => (
                        <Input
                            key={index}
                            className="mb-2"
                            addonBefore={`{{${index + 1}}}`}
                            value={value}
                            placeholder={`Value for variable ${index + 1}`}
                            onChange={(e) => {
                                const next = [...bodyParams];
                                next[index] = e.target.value;
                                setBodyParams(next);
                            }}
                        />
                    ))}
                </Form.Item>
            )}

            {mediaFormat && (
                <Row gutter={12}>
                    <Col span={8}>
                        <Form.Item label={`${mediaFormat} source`}>
                            <Select
                                value={headerMedia.source}
                                onChange={(v) => setHeaderMedia((m) => ({ ...m, source: v, value: '', name: '' }))}
                                options={[
                                    { value: 'url', label: 'Media URL' },
                                    { value: 'id', label: 'Select from custom media' },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={mediaFormat === 'DOCUMENT' && headerMedia.source === 'url' ? 8 : 16}>
                        {headerMedia.source === 'url' ? (
                            <Form.Item label="Media URL">
                                <Input
                                    value={headerMedia.value}
                                    placeholder="https://example.com/file"
                                    onChange={(e) => setHeaderMedia((m) => ({ ...m, value: e.target.value }))}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item label="Custom Media">
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Input
                                        disabled
                                        value={headerMedia.name || headerMedia.value}
                                        placeholder="No media selected"
                                    />
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        onClick={() => setOpenMediaModel(true)}
                                    >
                                        Select Media
                                    </Button>
                                </div>
                            </Form.Item>
                        )}
                    </Col>
                    {mediaFormat === 'DOCUMENT' && headerMedia.source === 'url' && (
                        <Col span={8}>
                            <Form.Item label="File name">
                                <Input
                                    value={headerMedia.name}
                                    placeholder="invoice.pdf"
                                    onChange={(e) => setHeaderMedia((m) => ({ ...m, name: e.target.value }))}
                                />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            )}
        </>
    );

    return (
        <Card title="Chatbot Idle Timer Response">
            <SelectMediaModel
                openMediaModel={openMediaModel}
                handleCancelMediaModel={() => setOpenMediaModel(false)}
                handleCustomMedia={handleMediaSelect}
                mediaKindFilter={mediaFormat?.toLowerCase()}
                requestType="Custom"
            />
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Row gutter={16} align="middle">
                    <Col span={24}>
                        <Form.Item label="Set Idle Timer Response">
                            <Switch checked={showFields} onChange={(checked) => setShowFields(checked)} />
                        </Form.Item>
                    </Col>
                    {showFields && (
                        <>


                        </>
                    )}
                </Row>

                {showFields && (
                    <>
                        <Row gutter={24}>
                            <Col>
                                <Form.Item
                                    label="Timer (in minutes)"
                                    name="minutes"
                                    rules={[{ required: true, message: 'Please select the timer duration!' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={999}
                                        className="w-75"
                                        value={minutes}
                                        onChange={(value) => {
                                            if (!isNaN(value) && value.toString().length <= 3) {
                                                setMinutes(value);
                                            }
                                        }}
                                        onKeyDown={(event) => {
                                            if (!/[0-9]/.test(event.key) && event.key !== 'Backspace') {
                                                event.preventDefault();
                                            }
                                        }}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col>
                                <Form.Item label="Message Type">
                                    <Radio.Group value={messageType} onChange={(e) => setMessageType(e.target.value)}>
                                        <Radio value="text">Text</Radio>
                                        <Radio value="template">Template</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            <Col>
                                <Form.Item label="Activate Session On New Message">
                                    <Switch checked={resetOnNewMesage} onChange={(checked) => setResetOnNewMesage(checked)} />
                                </Form.Item>
                            </Col>
                            {messageType === 'template' && (
                                <Col>
                                    <Form.Item label="Template Category">
                                        <Radio.Group value={templateCategory} onChange={handleCategoryChange}>
                                            <Radio value="MARKETING">Marketing</Radio>
                                            <Radio value="UTILITY">Utility</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>

                        {messageType === 'text' && (
                            <Form.Item label="Enter Your Message">
                                <TextArea
                                    rows={4}
                                    value={customMessage}
                                    onChange={(e) => setCustomMessage(e.target.value)}
                                />
                            </Form.Item>
                        )}

                        {messageType === 'template' && (
                            <Row gutter={24}>
                                <Col xs={24} md={12}>{renderTemplateConfig()}</Col>
                                <Col xs={24} md={12}>
                                    {templateSections ? (
                                        <>
                                            <NewMobilePreview
                                                requestType="template"
                                                template={templateSections}
                                                mediaLink={headerMedia.value}
                                                mediaRequired={true}
                                            />
                                        </>
                                    ) : (
                                        <Text type="secondary">Select a template to see a preview.</Text>
                                    )}
                                </Col>
                            </Row>
                        )}
                    </>
                )}
                <Form.Item className="float-right mt-2">
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Submit
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default ChatBotIdealTimer;
